import type { AnnotationCoordinate, AnnotationTextStyle } from './model';
import { mapLibreLabelFontLoadRequest } from '../map/mapLibreLabelFonts';
import { annotationTypography, hasModernAnnotationStyle } from './typography';

export interface AnnotationScreenPoint {
  readonly x: number;
  readonly y: number;
}

export interface AnnotationScreenProjection {
  readonly project: (coordinate: AnnotationCoordinate) => AnnotationScreenPoint;
  readonly unproject: (point: AnnotationScreenPoint) => AnnotationCoordinate;
  /** Counter MapLibre's pitched symbol-size perspective; never changes the authored style. */
  readonly billboardScaleAt?: (coordinate: AnnotationCoordinate) => number;
}

export interface TextBoxLayout {
  /** Effective content width, including the minimum for one glyph; excludes surrounding insets. */
  readonly contentWidth: number;
  readonly contentHeight: number;
  readonly textHeight: number;
  readonly textWidth: number;
  /** Padding plus border on one side of the content box. */
  readonly inset: number;
  /** Outer rendered dimensions, including padding and border. */
  readonly width: number;
  readonly height: number;
  readonly lineCount: number;
  readonly lines: readonly string[];
}

export interface TextBoxBounds {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
  readonly centre: AnnotationScreenPoint;
  readonly layout: TextBoxLayout;
}

/** Deterministic plain-text wrapping used by vector export and screen-space geometry estimates. */
export function wrapAnnotationText(
  text: string,
  maximumWidth: number,
  measure: (candidate: string) => number,
  breakLongInitialWord = false,
): readonly string[] {
  const lines: string[] = [];
  for (const paragraph of text.replace(/\r\n?/g, '\n').split('\n')) {
    if (!paragraph) {
      lines.push('');
      continue;
    }
    const words = paragraph.split(/\s+/);
    let line = '';
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if ((!line && !breakLongInitialWord) || measure(candidate) <= maximumWidth) {
        line = candidate;
        continue;
      }
      if (line) lines.push(line);
      if (measure(word) <= maximumWidth) {
        line = word;
        continue;
      }
      let fragment = '';
      for (const character of [...word]) {
        if (fragment && measure(`${fragment}${character}`) > maximumWidth) {
          lines.push(fragment);
          fragment = character;
        } else fragment += character;
      }
      line = fragment;
    }
    lines.push(line);
  }
  return lines.length ? lines : [''];
}

let measurementContext: CanvasRenderingContext2D | null | undefined;
const layouts = new Map<string, TextBoxLayout>();
const glyphWidths = new Map<string, number>();
export function estimateTextBoxLayout(text: string, style: AnnotationTextStyle): TextBoxLayout {
  const modern = hasModernAnnotationStyle(style);
  const request = mapLibreLabelFontLoadRequest(annotationTypography(style));
  const ready =
    modern && typeof document !== 'undefined' && !!document.fonts?.check(request, 'A\u0100');
  const key = JSON.stringify([
    text,
    style.fontFamily,
    style.fontSize,
    style.fontWeight,
    style.fontStyle,
    style.boxWidth,
    style.boxHeight,
    style.padding,
    style.borderWidth,
    style.textHaloWidth,
    ready,
  ]);
  const cached = layouts.get(key);
  if (cached) return cached;
  if (measurementContext === undefined && typeof CanvasRenderingContext2D !== 'undefined')
    measurementContext = document.createElement('canvas').getContext('2d');
  const context = ready ? measurementContext : null;
  if (context) context.font = request;
  const measure = (candidate: string) =>
    context
      ? [...candidate].reduce((total, character) => {
          const key = `${request}:${character}`;
          let width = glyphWidths.get(key);
          if (width === undefined) {
            width = context.measureText(character).width / 48;
            if (glyphWidths.size >= 8192) glyphWidths.clear();
            glyphWidths.set(key, width);
          }
          return total + width * style.fontSize;
        }, 0)
      : [...candidate].reduce((width, character) => width + (character === ' ' ? 0.32 : 0.56), 0) *
        style.fontSize;
  const contentWidth = modern
    ? Math.max(style.boxWidth, ...[...new Set(text)].map(measure))
    : style.boxWidth;
  const lines = wrapAnnotationText(text, contentWidth, measure, modern);
  const textHeight = Math.max(style.fontSize * 1.2, lines.length * style.fontSize * 1.2);
  const contentHeight = Math.max(textHeight, style.boxHeight ?? 0);
  const inset =
    style.padding +
    style.borderWidth +
    (style.textHaloWidth ?? 0) +
    (style.fontStyle === 'italic' ? style.fontSize * 0.18 : 0);
  const result = {
    contentWidth,
    contentHeight,
    textHeight,
    textWidth: Math.max(...lines.map(measure)),
    inset,
    width: Math.ceil((contentWidth + inset * 2) * 2) / 2,
    height: Math.ceil((contentHeight + inset * 2) * 2) / 2,
    lineCount: lines.length,
    lines,
  };
  if (layouts.size >= 512) layouts.clear();
  layouts.set(key, result);
  return result;
}

export function textBoxCentre(
  anchor: AnnotationScreenPoint,
  width: number,
  alignment: AnnotationTextStyle['alignment'],
): AnnotationScreenPoint {
  return {
    x:
      alignment === 'left'
        ? anchor.x + width / 2
        : alignment === 'right'
          ? anchor.x - width / 2
          : anchor.x,
    y: anchor.y,
  };
}

/**
 * Authoritative outer display box. The geographic anchor is the outer left/right edge for those
 * alignments and the outer centre for centred text.
 */
export function textBoxBounds(
  anchor: AnnotationScreenPoint,
  text: string,
  style: AnnotationTextStyle,
): TextBoxBounds {
  const layout = estimateTextBoxLayout(text, style);
  const centre = textBoxCentre(anchor, layout.width, style.alignment);
  return {
    left: centre.x - layout.width / 2,
    right: centre.x + layout.width / 2,
    top: centre.y - layout.height / 2,
    bottom: centre.y + layout.height / 2,
    centre,
    layout,
  };
}

/** Intersects the centre-to-target ray with an axis-aligned rendered text box. */
export function calloutBoxEdgePoint(
  target: AnnotationScreenPoint,
  centre: AnnotationScreenPoint,
  width: number,
  height: number,
  cornerRadius = 0,
): AnnotationScreenPoint {
  const dx = target.x - centre.x;
  const dy = target.y - centre.y;
  if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) return centre;
  const radius = Math.min(Math.max(0, cornerRadius), width / 2, height / 2);
  const inside = (x: number, y: number) => {
    const absoluteX = Math.abs(x - centre.x);
    const absoluteY = Math.abs(y - centre.y);
    if (absoluteX > width / 2 || absoluteY > height / 2) return false;
    if (radius === 0 || absoluteX <= width / 2 - radius || absoluteY <= height / 2 - radius)
      return true;
    return (
      Math.hypot(absoluteX - (width / 2 - radius), absoluteY - (height / 2 - radius)) <= radius
    );
  };
  if (inside(target.x, target.y)) return target;
  if (radius > 0) {
    let insideScale = 0;
    let outsideScale = 1;
    for (let iteration = 0; iteration < 32; iteration += 1) {
      const scale = (insideScale + outsideScale) / 2;
      const point = { x: centre.x + dx * scale, y: centre.y + dy * scale };
      if (inside(point.x, point.y)) insideScale = scale;
      else outsideScale = scale;
    }
    return { x: centre.x + dx * insideScale, y: centre.y + dy * insideScale };
  }
  const scale = 1 / Math.max(Math.abs(dx) / (width / 2), Math.abs(dy) / (height / 2));
  return { x: centre.x + dx * scale, y: centre.y + dy * scale };
}

export function calloutLeaderCoordinates(
  target: AnnotationCoordinate,
  textPosition: AnnotationCoordinate,
  text: string,
  style: AnnotationTextStyle,
  projection?: AnnotationScreenProjection,
  vertical = false,
  borderOverlap = 0,
): readonly [AnnotationCoordinate, AnnotationCoordinate] {
  if (!projection) return [textPosition, target];
  const targetPoint = projection.project(target);
  const anchor = projection.project(textPosition);
  const bounds = calloutAttachmentBounds(anchor, text, style);
  const edge = calloutAttachmentPoint(
    targetPoint,
    bounds.centre,
    bounds.width,
    bounds.height,
    bounds.radius,
    vertical,
    borderOverlap,
  );
  return [projection.unproject(edge), target];
}

/** Invisible interaction boxes do not create an empty leader gap around halo-only lettering. */
export function calloutAttachmentBounds(
  anchor: AnnotationScreenPoint,
  text: string,
  style: AnnotationTextStyle,
) {
  const bounds = textBoxBounds(anchor, text, style);
  if (style.backgroundOpacity > 0 || style.borderWidth > 0)
    return {
      centre: bounds.centre,
      width: bounds.layout.width,
      height: bounds.layout.height,
      radius: style.cornerRadius,
    };
  const halo = style.textHaloWidth ?? 0;
  // Blank, centred labels still need a finite intersection for world-space leaders.
  const width = Math.max(1, bounds.layout.textWidth + halo * 2);
  const shift = (bounds.layout.contentWidth - bounds.layout.textWidth) / 2;
  const centreX =
    bounds.centre.x +
    (style.alignment === 'left' ? -shift : style.alignment === 'right' ? shift : 0);
  const left =
    style.alignment === 'left' ? Math.min(centreX - width / 2, anchor.x) : centreX - width / 2;
  const right =
    style.alignment === 'right' ? Math.max(centreX + width / 2, anchor.x) : centreX + width / 2;
  return {
    centre: { x: (left + right) / 2, y: bounds.centre.y },
    width: right - left,
    height: bounds.layout.textHeight + halo * 2,
    radius: 0,
  };
}

export function calloutAttachmentPoint(
  target: AnnotationScreenPoint,
  centre: AnnotationScreenPoint,
  width: number,
  height: number,
  radius: number,
  vertical = false,
  borderOverlap = 0,
): AnnotationScreenPoint {
  const attach = (): AnnotationScreenPoint => {
    if (!vertical || Math.abs(target.x - centre.x) > width / 2 + 0.05)
      return calloutBoxEdgePoint(target, centre, width, height, radius);
    const corner = Math.min(radius, width / 2, height / 2);
    const fromEdge = Math.max(0, width / 2 - Math.abs(target.x - centre.x));
    const inset =
      fromEdge >= corner
        ? 0
        : corner - Math.sqrt(Math.max(0, corner * corner - (corner - fromEdge) ** 2));
    if (Math.abs(target.y - centre.y) <= height / 2 - inset) return target;
    return { x: target.x, y: centre.y + (target.y > centre.y ? 1 : -1) * (height / 2 - inset) };
  };
  const edge = attach();
  const dx = edge.x - target.x,
    dy = edge.y - target.y;
  const length = Math.hypot(dx, dy);
  const overlap = Math.min(Math.max(0, borderOverlap), width / 8, height / 8);
  return length > 0.01 && overlap > 0
    ? { x: edge.x + (dx / length) * overlap, y: edge.y + (dy / length) * overlap }
    : edge;
}
